/**
 * Performance Scorecard Table Component (CR-6310)
 *
 * Displays the scorecard metrics as a grid of metric cards with drillable values.
 * Each value is clickable and either opens a drill-down modal or navigates
 * to the appropriate detail page.
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '../ui/card'
import { SECTIONS, SECTION_DRILLDOWN_CONFIG } from './performanceConstants'
import DrilldownTrigger from '../drilldown/DrilldownTrigger'
import { DrilldownFilters as ContextDrilldownFilters } from '../../contexts/DrilldownContext'

export interface SectionData {
  ytd: string
  month: string
  ytdLabel: string
  monthLabel: string
}

export type SectionDataMap = {
  line_orders: SectionData
  delivery_performance: SectionData
  cost: SectionData
  returns: SectionData
  quality: SectionData
  supplier_concerns: SectionData
}

export interface DrilldownFilters {
  supplierId: number | null
  supplierName: string
  ytdStartDate: string
  ytdEndDate: string
  monthStartDate: string
  monthEndDate: string
}

interface PerformanceScorecardTableProps {
  sectionData: SectionDataMap
  supplierName: string
  drilldownFilters?: DrilldownFilters
}

interface DrillableValueProps {
  value: string
  sectionKey: string
  period: 'ytd' | 'month'
  filters: DrilldownFilters
  supplierName: string
}

/**
 * Check if a value represents zero or empty data that shouldn't be drillable.
 * Handles various formats: "0", "£0", "$0", "0%", "N/A", "0 / X", etc.
 */
function isZeroOrEmpty(value: string): boolean {
  if (!value || value === 'N/A' || value === '-') return true

  // Remove currency symbols, commas, and whitespace for comparison
  const cleaned = value.replace(/[£$€¥,\s]/g, '')

  // Check for simple zero
  if (cleaned === '0' || cleaned === '0%' || cleaned === '0.00' || cleaned === '0.0') return true

  // Check for ratio format "0 / X" (e.g., "0 / 23" for line orders)
  const ratioMatch = cleaned.match(/^(\d+)\s*\/\s*\d+$/)
  if (ratioMatch && parseInt(ratioMatch[1], 10) === 0) return true

  return false
}

/**
 * Renders a clickable value that triggers drill-down or navigation.
 * Zero values are rendered as plain text (not clickable).
 */
function DrillableValue({ value, sectionKey, period, filters, supplierName }: DrillableValueProps) {
  const navigate = useNavigate()
  const config = SECTION_DRILLDOWN_CONFIG[sectionKey]?.[period]

  // Build navigation URL with filter placeholders replaced
  // Hooks must be called before any conditional returns
  const buildNavUrl = useCallback(() => {
    if (!config?.navUrl) return null
    const startDate = period === 'ytd' ? filters.ytdStartDate : filters.monthStartDate
    const endDate = period === 'ytd' ? filters.ytdEndDate : filters.monthEndDate
    return config.navUrl
      .replace('{supplier}', filters.supplierId?.toString() || '')
      .replace('{startDate}', startDate)
      .replace('{endDate}', endDate)
  }, [config, filters, period])

  // Handle click for navigation-based drilldown
  const handleNavClick = useCallback(() => {
    const url = buildNavUrl()
    if (url) navigate(url)
  }, [buildNavUrl, navigate])

  // If value is zero or empty, render as plain text (not clickable)
  if (isZeroOrEmpty(value)) {
    return <span>{value}</span>
  }

  // If no drilldown config, just show the value
  if (!config) {
    return <span>{value}</span>
  }

  // Navigation-based drilldown
  if (config.navUrl) {
    return (
      <button
        onClick={handleNavClick}
        className="text-primary hover:underline cursor-pointer font-medium"
        title="Click to view details"
      >
        {value}
      </button>
    )
  }

  // Metric-based drilldown using DrilldownTrigger
  if (config.metric) {
    const startDate = period === 'ytd' ? filters.ytdStartDate : filters.monthStartDate
    const endDate = period === 'ytd' ? filters.ytdEndDate : filters.monthEndDate
    const drilldownFilters: ContextDrilldownFilters = {
      date_from: startDate,
      date_to: endDate,
      ...(filters.supplierId ? { supplier: filters.supplierId } : {}),
      // extraFilters values are strings (e.g., 'true' not true)
      ...(config.extraFilters || {}),
    }

    const contextLabel = `${supplierName} - ${config.labelSuffix || ''}`

    return (
      <DrilldownTrigger
        metric={config.metric}
        filters={drilldownFilters}
        contextLabel={contextLabel}
      >
        <span className="text-primary hover:underline cursor-pointer font-medium">
          {value}
        </span>
      </DrilldownTrigger>
    )
  }

  return <span>{value}</span>
}

interface MetricValueProps {
  periodLabel: string
  value: string
  sectionKey: string
  period: 'ytd' | 'month'
  filters: DrilldownFilters
  supplierName: string
  drilldownEnabled: boolean
}

/** One period column of a metric card: period caption + large value. */
function MetricValue({ periodLabel, value, sectionKey, period, filters, supplierName, drilldownEnabled }: MetricValueProps) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{periodLabel}</div>
      <div className="text-2xl font-semibold tabular-nums leading-tight truncate" title={value}>
        {drilldownEnabled ? (
          <DrillableValue value={value} sectionKey={sectionKey} period={period} filters={filters} supplierName={supplierName} />
        ) : (
          value
        )}
      </div>
    </div>
  )
}

/**
 * Scorecard metrics as a grid of cards — one per section, each showing the
 * YTD and Month figures side by side.
 *
 * The previous layout was a six-band table: a full-width coloured header
 * row per section, then a row of label / value / label / value. Reading it
 * meant scanning across the whole page width for two numbers, and the bands
 * carried more colour than information. A card per section keeps each
 * metric's two figures together, uses colour only as a thin accent, and
 * lets the eye compare YTD with the month at a glance.
 */
export function PerformanceScorecardTable({
  sectionData,
  supplierName,
  drilldownFilters,
}: PerformanceScorecardTableProps) {
  // Default filters if not provided (drill-down disabled)
  const filters: DrilldownFilters = drilldownFilters || {
    supplierId: null,
    supplierName: supplierName,
    ytdStartDate: '',
    ytdEndDate: '',
    monthStartDate: '',
    monthEndDate: '',
  }

  const isDrilldownEnabled = !!drilldownFilters

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
      {SECTIONS.map((section) => {
        const data = sectionData[section.key as keyof SectionDataMap]
        if (!data) return null
        // "Lines Delivered/Expected YTD" → the card already says YTD / Month,
        // so the caption is the metric name without the period suffix.
        const metricName = data.ytdLabel.replace(/\s+YTD$/i, '')

        return (
          <Card key={section.key} className="shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className="flex">
                <div className={`w-1.5 shrink-0 ${section.accentBar}`} aria-hidden="true" />
                <div className="flex-1 min-w-0 p-4">
                  <div className="flex items-baseline justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${section.accentText}`}>{section.label}</div>
                      <div className="text-xs text-muted-foreground truncate" title={metricName}>{metricName}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 divide-x divide-border">
                    <MetricValue
                      periodLabel="Year to date"
                      value={data.ytd}
                      sectionKey={section.key}
                      period="ytd"
                      filters={filters}
                      supplierName={supplierName}
                      drilldownEnabled={isDrilldownEnabled}
                    />
                    <div className="pl-4">
                      <MetricValue
                        periodLabel="Month"
                        value={data.month}
                        sectionKey={section.key}
                        period="month"
                        filters={filters}
                        supplierName={supplierName}
                        drilldownEnabled={isDrilldownEnabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
